const EVM_ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;
const TX_HASH_RE = /^0x[a-fA-F0-9]{64}$/;
const SOLANA_ADDRESS_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/;
const SLACK_MRKDWN_RE = /<https?:\/\/[^|>]+\|[^>]+>|<@[UW][A-Z0-9]+>|<#C[A-Z0-9]+\|?[^>]*>/;
const TELEGRAM_HTML_RE = /<\/?(b|strong|i|em|u|s|code|pre|a)(\s[^>]*)?>/i;
const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|svg|avif)(\?|#|$)/i;

export function isEvmAddress(v: unknown): v is string {
  return typeof v === "string" && EVM_ADDRESS_RE.test(v);
}

export function isTxHash(v: unknown): v is string {
  return typeof v === "string" && TX_HASH_RE.test(v);
}

export function isSolanaAddress(v: unknown): v is string {
  // Exclude 0x-prefixed strings (those are EVM); require base58 alphabet, 32-44 chars.
  return typeof v === "string" && !v.startsWith("0x") && SOLANA_ADDRESS_RE.test(v);
}

export function isIsoDate(v: unknown): v is string {
  if (typeof v !== "string" || !ISO_DATE_RE.test(v)) return false;
  return !Number.isNaN(Date.parse(v));
}

export function isEpochMs(v: unknown): v is number {
  // 13-digit millisecond timestamps roughly between 2001 and 2286.
  return typeof v === "number" && Number.isInteger(v) && v >= 1_000_000_000_000 && v <= 9_999_999_999_999;
}

export function looksLikeMarkdown(v: unknown): boolean {
  if (typeof v !== "string" || v.length < 12) return false;
  const hasHeading = /^#{1,6}\s/m.test(v);
  const hasBold = /\*\*[^*\n]+\*\*/.test(v);
  const hasTable = /^\s*\|.+\|\s*$/m.test(v) && /^\s*\|[\s\-:|]+\|\s*$/m.test(v);
  const hasList = /^\s*([-*+]|\d+\.)\s+\S/m.test(v);
  const hasFence = /```/.test(v);
  return hasHeading || hasBold || hasTable || hasList || hasFence;
}

export function looksLikeSlackMrkdwn(v: unknown): boolean {
  return typeof v === "string" && SLACK_MRKDWN_RE.test(v);
}

// Broad heuristic — intentionally permissive. Callers (renderer registry) are
// expected to gate this behind a node-type hint or a low confidence match so
// generic HTML over-matching is handled at the call site, not here.
export function looksLikeTelegramHtml(v: unknown): boolean {
  return typeof v === "string" && TELEGRAM_HTML_RE.test(v);
}

export function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export function isArrayOfObjects(v: unknown): v is Record<string, unknown>[] {
  if (!Array.isArray(v) || v.length === 0) return false;
  if (!v.every(isPlainObject)) return false;
  // Two-threshold consistency check:
  //   1. Find "shared" keys: keys present in >= 60% of rows.
  //   2. Require that shared keys make up >= 60% of the union of all keys.
  // This allows sparse but mostly-uniform tables, while rejecting fully
  // heterogeneous arrays where every row has different keys.
  const keyCounts = new Map<string, number>();
  for (const row of v) {
    for (const k of Object.keys(row)) keyCounts.set(k, (keyCounts.get(k) ?? 0) + 1);
  }
  const sharedKeys = [...keyCounts.values()].filter(count => count >= v.length * 0.6).length;
  return sharedKeys >= Math.max(1, keyCounts.size * 0.6);
}

export function isFlatScalarObject(v: unknown): v is Record<string, unknown> {
  if (!isPlainObject(v)) return false;
  const entries = Object.entries(v);
  if (entries.length === 0) return false;
  return entries.every(([, val]) => val === null || typeof val !== "object");
}

export type NumberClass = "currency" | "percent" | "count" | "plain";

/** Split a camelCase/snake_case/kebab-case key into lowercase words. */
function keyWords(key: string): string[] {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[\s_-]+/)
    .map(w => w.toLowerCase())
    .filter(Boolean);
}

const COUNT_WORDS = new Set(["tokens", "count", "total", "supply"]);

export function classifyNumberKey(key: string | undefined): NumberClass {
  if (!key) return "plain";
  const k = key.toLowerCase();
  // Check percent/currency via substring regex (existing behavior, false positives
  // here are acceptable — these patterns are naturally bounded in practice).
  // percent must be checked before currency (e.g. "priceChangePercentage24h").
  if (/(percentage|percent|pct|rate|drawdown)/.test(k)) return "percent";
  if (/(cost|price|usd|fee|amountusd|valueusd)/.test(k)) return "currency";
  // Count words use word-boundary splitting to avoid substring over-matching
  // (e.g. "discount" contains "count", "account" contains "count",
  // "subtotal" contains "total").
  if (keyWords(key).some(w => COUNT_WORDS.has(w))) return "count";
  return "plain";
}

export function isImageUrl(v: unknown): v is string {
  return typeof v === "string" && /^https?:\/\//.test(v) && IMAGE_EXT_RE.test(v);
}

export function isHttpUrl(v: unknown): v is string {
  return typeof v === "string" && /^https?:\/\/\S+$/.test(v);
}

/** Matches node types that are LLM / AI / formatting / logging in nature.
 *  Used by primary-content.ts and MarkdownRenderer to prioritise text content.
 *  Anchored on `-`/`_`/string boundaries so substrings like "catalog",
 *  "dialog" or "changelog" don't falsely match "log". */
export const LLM_NODE_TYPE_RE = /(^|[-_])(llm|ai|format|log|agent|chat)([-_]|$)/i;
